import { ArrowUpRight, Compass, SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  PaletteHint,
  ResultSkeleton,
  SearchPalette,
  SuggestionChips,
} from "@/components/search/SearchPalette";
import { MIN_SEARCH_LENGTH, useSearchTerm } from "@/components/search/use-search-term";
import { topicsSearchPath } from "@/components/search/search-mode";

import { useGuardedNavigate } from "@/hooks/use-guarded-navigate";
import { usePublicSessions } from "@/hooks/use-public-sessions";

export interface TopicSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Rows shown in the palette. The footer opens Browse for the rest. */
const TOPIC_RESULT_LIMIT = 5;

/**
 * A few real titles to start from. These are English on purpose: the topics are
 * English whatever the interface language is, and an example that does not look
 * like the thing being searched teaches nothing.
 */
const EXAMPLE_QUERIES = ["travel", "interview", "coffee"];

/**
 * "Which topic should I study?" — the search behind the sidebar's first row.
 *
 * A launcher, not a results page: a handful of live matches so the answer is
 * often one tap away, and the footer hands the query to Browse, which can page
 * through all of them.
 */
export function TopicSearchDialog({ open, onOpenChange }: TopicSearchDialogProps) {
  const { t } = useTranslation();
  const guardedNavigate = useGuardedNavigate();

  const { term, setTerm, trimmed, liveTerm, pending, canSearch, canSubmit, typingTooShort } =
    useSearchTerm(open);

  const topics = usePublicSessions({
    pageSize: TOPIC_RESULT_LIMIT,
    searchTerm: open && canSearch ? trimmed : "",
    enabled: open && canSearch,
  });

  const rows = topics.data?.data ?? [];
  const total = topics.data?.total ?? 0;

  // The topic list keeps the previous term's rows while the next request is in
  // flight (`placeholderData`), so the list dims instead of flashing.
  const isStale = pending || topics.isPlaceholderData;

  const go = (path: string) => {
    onOpenChange(false);
    void guardedNavigate(path);
  };

  const submit = () => {
    if (!canSubmit) return;
    go(topicsSearchPath(liveTerm));
  };

  const footerLabel = (() => {
    if (!canSubmit) return t("nav.search.footerIdle", "Type to search");
    if (pending) {
      return t("nav.search.searchAllTopics", {
        query: liveTerm,
        defaultValue: "Search all topics for “{{query}}”",
      });
    }
    // The server's total counts rows the list may still drop (a topic whose
    // generation stopped halfway is not shown), so an empty list must not
    // advertise "see all N" — the two would contradict each other.
    return rows.length > 0 && total > rows.length
      ? t("nav.search.seeAllTopics", {
          count: total,
          query: trimmed,
          defaultValue: "See all {{count}} results for “{{query}}”",
        })
      : t("nav.search.searchAllTopics", {
          query: trimmed,
          defaultValue: "Search all topics for “{{query}}”",
        });
  })();

  return (
    <SearchPalette
      open={open}
      onOpenChange={onOpenChange}
      icon={Compass}
      title={t("nav.search.topicsTitle", "Search topics")}
      description={t(
        "nav.search.topicsDescription",
        "Find a topic to practise by title or category.",
      )}
      placeholder={t("nav.search.topicsPlaceholder", "Search topics...")}
      term={term}
      onTermChange={setTerm}
      onSubmit={submit}
      footerLabel={footerLabel}
      footerDisabled={!canSubmit}
      onFooterClick={submit}
      bodyClassName={isStale && canSearch ? "overflow-y-auto opacity-50" : "overflow-y-auto"}
    >
      {typingTooShort ? (
        <PaletteHint
          icon={SearchX}
          title={t("nav.search.tooShort", {
            count: MIN_SEARCH_LENGTH,
            defaultValue: "Type at least {{count}} characters.",
          })}
        />
      ) : !liveTerm ? (
        <PaletteHint
          icon={Compass}
          title={t("nav.search.topicsHint", "Search topics by title or category.")}
          description={t(
            "nav.search.topicsHintSecondary",
            "Every result opens a real lesson you can practise from.",
          )}
        >
          <SuggestionChips
            label={t("nav.search.tryExamples", "Try")}
            words={EXAMPLE_QUERIES}
            onPick={setTerm}
          />
        </PaletteHint>
      ) : !canSearch ? (
        <ResultSkeleton />
      ) : topics.isLoading ? (
        <ResultSkeleton />
      ) : topics.isError ? (
        <PaletteHint
          icon={SearchX}
          title={t("nav.search.topicsError", "Could not search topics right now.")}
        />
      ) : rows.length === 0 ? (
        <PaletteHint
          icon={SearchX}
          title={t("nav.search.topicsEmpty", {
            query: trimmed,
            defaultValue: "No topics match “{{query}}”.",
          })}
          description={t(
            "nav.search.topicsNoResultsHint",
            "Topics are matched by title — try a shorter word, or open Browse to filter by category.",
          )}
        />
      ) : (
        <ul className="divide-y divide-stone-200/60 dark:divide-stone-800/70">
          {rows.map((session) => {
            const questionCount = Array.isArray(session.questions)
              ? session.questions.length
              : 0;

            return (
              <li key={session.id}>
                <button
                  type="button"
                  onClick={() => go(`/session/${session.slug}`)}
                  className="group flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring dark:hover:bg-stone-800/50"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-stone-100 text-stone-500 transition-colors group-hover:bg-primary/10 group-hover:text-primary-text dark:bg-stone-800 dark:text-stone-400">
                    <Compass className="h-4 w-4" aria-hidden />
                  </span>

                  <span className="min-w-0 flex-1">
                    {/* The topic is subject matter, so it is set in the serif —
                        the same rule the deck and the topic cards follow. */}
                    <span className="block truncate font-display text-[15px] font-semibold tracking-tight text-stone-900 dark:text-stone-50">
                      {session.topic}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                      {session.category && (
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 font-medium text-stone-600 dark:bg-stone-800 dark:text-stone-400">
                          {session.category}
                        </span>
                      )}
                      {questionCount > 0 && (
                        <span>
                          {t("common.count.question", {
                            count: questionCount,
                            defaultValue: "{{count}} questions",
                          })}
                        </span>
                      )}
                    </span>
                  </span>

                  <ArrowUpRight
                    className="mt-1.5 h-3.5 w-3.5 shrink-0 text-stone-300 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 dark:text-stone-600"
                    aria-hidden
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </SearchPalette>
  );
}

export default TopicSearchDialog;
